import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { Button, ConfigProvider } from "antd";
import { Helmet } from "react-helmet";
import {
  DoubleSidebar,
  useDoubleSidebar,
  useGlobal,
  useAccount
} from "@vojomo-core/shared";
import { MobileHeader, MobileNavigation } from "@vojomo-core/shared/src/components/Mobile";
import { Footer } from "@vojomo-core/shared/src/components/Footer/Footer";
import { mainLinksData, createLinksData } from "../../config/routeConfig";
import styles from "./Home.module.css";
import {
  ArrowRight,
  Target,
  Sparkles,
  Clock,
  Layers,
  Eye,
  Mountain,
  ChevronDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { NewRoadmapForm } from "../../components/NewRoadmapForm/NewRoadmapForm";

const SERVER_URL = import.meta.env.VITE_REACT_APP_SERVER || "";

const buildingBlockIcons = [
  { icon: Eye, label: "Vision", color: "#f37037" },
  { icon: Target, label: "Mission", color: "#2c1561" },
  { icon: Mountain, label: "Peak Ambition", color: "#802a88" },
  { icon: Layers, label: "Objectives", color: "#ef4269" },
];

export function Home(): React.ReactElement {
  const { isCollapsed } = useDoubleSidebar();
  const { isMobile } = useGlobal();
  const { vojomoAccount } = useAccount();
  const linksData = createLinksData();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const wordmarkUrl = new URL(
    `../../../../shared/src/static/Vojomo_Horiz_fullcolor_rgb.png`,
    import.meta.url
  ).href;

  const [showForm, setShowForm] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);

  const isCoordinator =
    (vojomoAccount?.company_coordinator_companies?.length ?? 0) > 0 ||
    !!vojomoAccount?.is_admin ||
    vojomoAccount?.company === "Vojomo";

  const filteredMainLinksData = useMemo(
    () => mainLinksData.filter((item) => item.title !== "Coordinator" || isCoordinator),
    [isCoordinator]
  );

  useEffect(() => {
    fetch(`${SERVER_URL}/api/rb/sessions`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : [])
      .then((data: unknown[]) => setSessionCount(data.length))
      .catch(() => {});
  }, []);

  const contentMarginLeft = isMobile ? 0 : isCollapsed ? 100 : 250;
  const viewportHeight = isMobile ? "calc(100vh - 56px)" : "100vh";

  return (
    <div className={styles.body}>
      <Helmet>
        <title>Vojomo</title>
      </Helmet>

      {isMobile && (
        <MobileHeader onMenuClick={() => setMobileNavOpen(true)} showLogo={true} />
      )}
      {isMobile && (
        <MobileNavigation
          isOpen={mobileNavOpen}
          onClose={() => setMobileNavOpen(false)}
          mainLinksData={filteredMainLinksData}
          linksData={linksData}
        />
      )}
      {!isMobile && (
        <DoubleSidebar
          mainLinksData={filteredMainLinksData}
          linksData={linksData}
          activeName="Vojomo"
        />
      )}

      {/* Main content */}
      <div
        style={{
          marginLeft: contentMarginLeft,
          transition: "margin-left 0.1s ease",
          height: viewportHeight,
          paddingTop: isMobile ? "56px" : 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <ConfigProvider theme={{ token: { colorPrimary: "#2c1561" } }}>
          {/* Top-level AnimatePresence: hero view ↔ form view */}
          <AnimatePresence mode="wait">

            {/* ── Hero view ── */}
            {!showForm && (
              <motion.div
                key="hero"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.2 }}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  overflowY: "auto",
                  padding: isMobile ? "2rem 1.25rem 5rem" : "2rem 1.5rem 5rem",
                }}
              >
                <div style={{ width: "100%", maxWidth: 520 }}>
                  {/* Wordmark */}
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    style={{ marginBottom: 28, textAlign: "center" }}
                  >
                    <img src={wordmarkUrl} style={{ width: isMobile ? 140 : 180 }} />
                  </motion.div>

                  {/* Hero copy */}
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    style={{ textAlign: "center", marginBottom: 28 }}
                  >
                    <h1
                      style={{
                        fontSize: isMobile ? 24 : 30,
                        fontWeight: 700,
                        color: "#1e1b4b",
                        lineHeight: 1.25,
                        marginBottom: 12,
                        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      Build your strategic roadmap{" "}
                      <span style={{ whiteSpace: "nowrap" }}>
                        in <span style={{ color: "#802a88" }}>~10 minutes</span>
                      </span>
                    </h1>
                    <p
                      style={{
                        fontSize: isMobile ? 14 : 15,
                        color: "#475569",
                        lineHeight: 1.65,
                        maxWidth: 420,
                        margin: "0 auto 16px",
                      }}
                    >
                      Vo, your AI thought partner, researches your subject, gathers your
                      insights, and drafts a complete 7-block roadmap — for a company,
                      product, book, or any initiative.
                    </p>

                    {/* Feature chips */}
                    <div style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 12 }}>
                      {[
                        { icon: Clock, text: "~10 min to first draft" },
                        { icon: Layers, text: "7 strategic blocks" },
                        { icon: Sparkles, text: "AI-reasoned insights" },
                      ].map((item) => (
                        <div
                          key={item.text}
                          style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: "#64748b" }}
                        >
                          <item.icon style={{ width: 12, height: 12, color: "#2c1561" }} />
                          <span>{item.text}</span>
                        </div>
                      ))}
                    </div>
                  </motion.div>

                  {/* CTA */}
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                    style={{ textAlign: "center" }}
                  >
                    <Button
                      type="primary"
                      size="large"
                      style={{ height: 50, paddingInline: 32, fontSize: 15, fontWeight: 600, borderRadius: 10 }}
                      onClick={() => setShowForm(true)}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        Start a new roadmap
                        <ArrowRight style={{ width: 16, height: 16 }} />
                      </span>
                    </Button>

                    {sessionCount > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <Link
                          to="/roadmap/myroadmaps"
                          style={{ fontSize: 13, color: "#64748b", display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }}
                        >
                          Open my roadmaps
                          <span style={{ fontSize: 11, background: "rgba(255,255,255,0.7)", border: "1px solid #e2e8f0", borderRadius: 999, padding: "1px 7px", color: "#64748b" }}>
                            {sessionCount}
                          </span>
                          <ChevronDown style={{ width: 12, height: 12, transform: "rotate(-90deg)" }} />
                        </Link>
                      </div>
                    )}
                  </motion.div>

                  {/* Building block chips */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                    style={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 8, marginTop: 24 }}
                  >
                    {buildingBlockIcons.map((block, i) => (
                      <motion.div
                        key={block.label}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.45 + i * 0.07 }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "4px 10px",
                          borderRadius: 8,
                          background: "rgba(255,255,255,0.65)",
                          fontSize: 12,
                          fontWeight: 500,
                          color: "#334155",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
                        }}
                      >
                        <block.icon style={{ width: 11, height: 11, color: block.color, flexShrink: 0 }} />
                        <span>{block.label}</span>
                      </motion.div>
                    ))}
                  </motion.div>

                  {/* How It Works */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    style={{ marginTop: 40 }}
                  >
                    <div style={{ borderTop: "1px solid rgba(255,255,255,0.4)", paddingTop: 28 }}>
                      <p style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "#94a3b8", textAlign: "center", marginBottom: 20 }}>
                        How it works
                      </p>
                      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 16 }}>
                        {[
                          { step: "01", title: "Tell Vo what you're building", desc: "Enter a name and any context. Share links, or describe your vision." },
                          { step: "02", title: "Vo researches & synthesizes", desc: "Vo pulls public data and maps everything to 7 strategic building blocks." },
                          { step: "03", title: "Review your draft roadmap", desc: "Get a structured roadmap with AI-reasoned insights and clear gaps to fill." },
                        ].map((item) => (
                          <div key={item.step} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            <span style={{ fontSize: 10, fontFamily: "monospace", fontWeight: 700, color: "#2c1561" }}>{item.step}</span>
                            <p style={{ fontSize: 12, fontWeight: 600, color: "#334155", margin: 0, fontFamily: "system-ui, -apple-system, sans-serif" }}>{item.title}</p>
                            <p style={{ fontSize: 11, color: "#64748b", margin: 0, lineHeight: 1.55 }}>{item.desc}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </div>

                {/* Footer */}
                <div style={{ position: "fixed", bottom: 0, left: contentMarginLeft, right: 0, transition: "left 0.1s ease", zIndex: 5 }}>
                  <Footer />
                </div>
              </motion.div>
            )}

            {/* ── Form view ── */}
            {showForm && (
              <motion.div
                key="form"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.2 }}
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                  overflowY: "auto",
                  padding: isMobile ? "2rem 1.25rem 4rem" : "2rem 1.5rem 4rem",
                }}
              >
                <div style={{ width: "100%", maxWidth: 540 }}>
                  <NewRoadmapForm onBack={() => setShowForm(false)} />
                </div>
              </motion.div>
            )}

          </AnimatePresence>
        </ConfigProvider>
      </div>
    </div>
  );
}
